import { useQuery } from '@tanstack/react-query';
import type { PharmacyPagedListResponseT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { fetchSearchPharmacies, type SearchPharmaciesInput } from '../api/searchPharmacies.js';
import { rxKeys } from './keys.js';

export function searchPharmaciesQueryDefinition(input: SearchPharmaciesInput) {
  return {
    queryKey: rxKeys.pharmaciesSearch(input),
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchSearchPharmacies(input, signal),
  } as const;
}

/**
 * Whether ``input`` holds something DoseSpot can search on. DoseSpot accepts a
 * search by city, ZIP, address, phone or NCPDP ID as well as by name, so any
 * one of them is enough; state, specialty and page only narrow a search.
 */
export function hasPharmacySearchTerm(input: SearchPharmaciesInput): boolean {
  const terms = [input.name, input.city, input.zip, input.address, input.phoneOrFax, input.ncpdpID];
  return terms.some((term) => (term?.trim() ?? '') !== '');
}

/**
 * Search pharmacies for ``input``; pass undefined while there is nothing to
 * search for. The caller decides when its terms are worth searching for (for
 * example a minimum length); the hook still never sends a search without a
 * term, which would return every pharmacy or an empty page.
 */
export function useSearchPharmaciesQuery(input: SearchPharmaciesInput | undefined) {
  const enabled = input !== undefined && hasPharmacySearchTerm(input);

  return useQuery({
    queryKey: input ? rxKeys.pharmaciesSearch(input) : (['rx', 'pharmacy', 'search', ''] as const),
    queryFn: ({ signal }: { signal?: AbortSignal }): Promise<PharmacyPagedListResponseT> => {
      if (!input) {
        throw new Error('Missing pharmacy search input');
      }
      return fetchSearchPharmacies(input, signal);
    },
    enabled,
  });
}
